import {
  ColDef,
  ICellRendererParams,
  ValueGetterParams,
} from 'ag-grid-community';
import Typo from '@/components/atom/Typo';
import getTableIndex from '@/src/utils/getTableIndex';
import TableRowStatusBadge, {
  TableBadgeType,
} from '@/components/molecule/TableRowStatusBadge';
import dayjs from 'dayjs';
import { formatter } from '@/src/utils/formatter';
import { FormField } from '@/components/ui/form';
import Input from '@/components/atom/Input';
import { UseFormReturn } from 'react-hook-form';
import Button from '@/components/atom/Button';
import React from 'react';
import Badge, { badgeColorData } from '@/src/components/atom/Badge';
import ImagePreview from '@/components/molecule/ImagePreview';

export type ColSizeType = 'xs' | 'sm' | 'md' | 'lg';

interface createColProps extends ColDef {
  field: string;
  size?: ColSizeType;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
}

export const ColSize: {
  [key in ColSizeType]: {
    width?: number;
    minWidth?: number;
    maxWidth?: number;
    flex?: number;
  };
} = {
  xs: {
    width: 90,
    minWidth: 90,
    maxWidth: 90,
    flex: 0,
  },
  sm: {
    width: 140,
    minWidth: 140,
    maxWidth: 200,
    flex: 0,
  },
  md: {
    width: 240,
    minWidth: 240,
    maxWidth: 300,
    flex: 0,
  },
  lg: {
    minWidth: 350,
    width: 350,
    flex: 1,
  },
} as const;

const MemoizedInput = React.memo(Input);
const MemoizedFormField = React.memo(FormField);

export const cellRenderer = {
  INDEX: ({ data, node }: ValueGetterParams) => {
    const value: number = node?.rowIndex
      ? getTableIndex(node?.rowIndex + 1, data.page, data.pageSize)
      : getTableIndex(1, data.page, data.pageSize);
    return _renderText(value);
  },
  TEXT: ({ value, valueFormatted }: ICellRendererParams) => {
    return _renderText(valueFormatted || value || '-');
  },
  DATE: ({ colDef, data }: ValueGetterParams) => {
    const field = colDef?.field;
    const format = 'YYYY-MM-DD';
    const value: string =
      field && data[field] ? dayjs(data[field]).format(format) : '-';
    return _renderText(value);
  },
  DATE_HAS_MIN: ({ colDef, data }: ValueGetterParams) => {
    const field = colDef?.field;
    const format = 'YYYY-MM-DD HH:mm';
    const value: string =
      field && data[field] ? dayjs(data[field]).format(format) : '-';
    return _renderText(value);
  },
  DATE_HAS_ALL_TIME: ({ colDef, data }: ValueGetterParams) => {
    const field = colDef?.field;
    const format = 'YYYY-MM-DD HH:mm:ss';
    const value: string =
      field && data[field] ? dayjs(data[field]).format(format) : '-';
    return _renderText(value);
  },
  NUMBER: ({ colDef, data }: ValueGetterParams) => {
    const field = colDef?.field;
    const value: number =
      field && !isNaN(Number(data[field])) ? data[field] : '-';

    return _renderText(
      isNaN(Number(value)) ? '-' : Number(value).toLocaleString(),
    );
  },
  PHONE: ({ colDef, data }: ValueGetterParams) => {
    const field = colDef?.field;
    const value: string =
      field && data[field] ? formatter.PHONE(data[field]) : '-';
    return _renderText(value);
  },
  KOR_NUMBER: ({ colDef, data }: ValueGetterParams) => {
    const field = colDef?.field;
    const value: string =
      field && data[field] ? formatter.KOR_NUMBER(data[field]) : '-';
    return _renderText(value);
  },
};

export const createIndexCol = (): ColDef => {
  return {
    headerName: 'No.',
    headerClass: 'ag-right-aligned-cell',
    field: 'no',
    lockPosition: 'left',
    pinned: 'left',
    cellStyle: { textAlign: 'center' },
    cellRenderer: cellRenderer.INDEX,
    width: 120,
    minWidth: 70,
  };
};

export const createIdCol = (): ColDef => {
  return {
    field: 'id',
    headerName: 'ID',
    headerClass: 'ag-right-aligned-cell',
    lockPosition: 'left',
    pinned: 'left',
    cellStyle: { textAlign: 'center' },
    cellRenderer: cellRenderer.NUMBER,
    width: 120,
    minWidth: 70,
  };
};

export const createTypoCol = ({ field, ...props }: createColProps): ColDef => {
  return {
    field,
    headerClass: 'ag-right-aligned-cell',
    cellStyle: { textAlign: 'center' },
    flex: 1,
    cellRenderer: cellRenderer.TEXT,
    ...(props?.size && ColSize[props.size]),
    ...props,
  };
};

export const createNumberCol = ({
  field,
  ...props
}: createColProps): ColDef => {
  return {
    field,
    headerClass: 'ag-right-aligned-cell',
    cellStyle: { textAlign: 'center' },
    flex: 1,
    cellRenderer: cellRenderer.NUMBER,
    ...(props?.size && ColSize[props.size]),
    ...props,
  };
};

export const createDateCol = ({ field, ...props }: createColProps): ColDef => {
  return {
    field,
    headerClass: 'ag-right-aligned-cell',
    cellStyle: { textAlign: 'center' },
    flex: 1,
    cellRenderer: cellRenderer.DATE,
    ...(props?.size && ColSize[props.size]),
    ...props,
  };
};

export const createDateHasMinCol = ({
  field,
  ...props
}: createColProps): ColDef => {
  return {
    field,
    headerClass: 'ag-right-aligned-cell',
    cellStyle: { textAlign: 'center' },
    flex: 1,
    cellRenderer: cellRenderer.DATE_HAS_MIN,
    ...(props?.size && ColSize[props.size]),
    ...props,
  };
};

export const createDateHasAllTimeCol = ({
  field,
  ...props
}: createColProps): ColDef => {
  return {
    field,
    headerClass: 'ag-right-aligned-cell',
    cellStyle: { textAlign: 'center' },
    flex: 1,
    cellRenderer: cellRenderer.DATE_HAS_ALL_TIME,
    ...(props?.size && ColSize[props.size]),
    ...props,
  };
};

export const createPhoneCol = ({ field, ...props }: createColProps): ColDef => {
  return {
    field,
    headerClass: 'ag-right-aligned-cell',
    cellStyle: { textAlign: 'center' },
    flex: 1,
    cellRenderer: cellRenderer.PHONE,
    ...(props?.size && ColSize[props.size]),
    ...props,
  };
};

export const createKorNumberCol = ({
  field,
  ...props
}: createColProps): ColDef => {
  return {
    field,
    headerClass: 'ag-right-aligned-cell',
    cellStyle: { textAlign: 'center' },
    flex: 1,
    cellRenderer: cellRenderer.KOR_NUMBER,
    ...(props?.size && ColSize[props.size]),
    ...props,
  };
};

export const createViewableCol = ({
  field,
  ...props
}: createColProps): ColDef => {
  return {
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    field,
    width: 96,
    minWidth: 96,
    maxWidth: 96,
    ...(props?.size && ColSize[props.size]),
    ...props,
    cellRenderer: (params: ValueGetterParams) => {
      if (params.data?.[field] === true || params.data?.[field] === false) {
        return (
          <TableRowStatusBadge type="isViewable" value={params.data?.[field]} />
        );
      } else {
        return '-';
      }
    },
  };
};

export const createStatusBadgeCol = ({
  field,
  type = 'isViewable',
  statusValueFormatter,
  ...props
}: createColProps & {
  type: TableBadgeType;
  statusValueFormatter?: (params: ValueGetterParams) => boolean | undefined;
}): ColDef => {
  return {
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    field,
    width: 96,
    minWidth: 96,
    maxWidth: 96,
    ...(props?.size && ColSize[props.size]),
    ...props,
    cellRenderer: (params: ValueGetterParams) => {
      const formattedValue =
        statusValueFormatter && statusValueFormatter(params);
      const isBoolValue =
        typeof formattedValue === 'boolean' ||
        typeof params.data?.[field] === 'boolean';

      if (isBoolValue) {
        return (
          <TableRowStatusBadge
            type={type}
            value={formattedValue || params.data?.[field]}
          />
        );
      } else {
        return '-';
      }
    },
  };
};

export const createDetailButtonColV2 = (
  onDetailClick: (value: number) => void,
  options?: Omit<createColProps, 'field'>,
): ColDef => {
  return {
    field: 'detail',
    lockPosition: 'right',
    pinned: 'right',
    headerName: '조회',
    headerClass: 'ag-right-aligned-cell',
    width: 129,
    minWidth: 129,
    maxWidth: 129,
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    cellRenderer: ({ data }: ICellRendererParams) => {
      return (
        <Button
          buttonSize="xSmall"
          buttonColor="outline"
          onClick={() => onDetailClick(data.id as number)}
        >
          상세보기
        </Button>
      );
    },
    ...options,
  };
};

/*
 * @description: 테이블에 입력을 위한 컬럼을 생성합니다.
 * @param {UseFormReturn<any>} form
 * @param {string} field (input의 field 명)
 * @param {string} tableField (input의 field가 특정 field에 배열로 들어가는 경우)
 */
export const createTextInputCol = ({
  form,
  field,
  placeholder,
  tableField,
  valueFormatter,
  headerName,
}: {
  form: UseFormReturn<any>;
  field: string;
  placeholder: string;
  tableField?: string;
  valueFormatter?: (value: string) => string;
  headerName: string;
}): ColDef => {
  return {
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    lockPosition: true,
    field: field,
    flex: 1,
    valueSetter: params => {
      if (!params?.colDef?.field || params?.newValue === undefined)
        return false;
      // 형변환을 위해 toString() 사용
      params.data[params.colDef.field] = params.newValue?.toString();
      return true;
    },
    cellRenderer: ({
      data,
      value,
      setValue,
      rowIndex,
    }: ICellRendererParams) => {
      const onChange = (targetValue: string) => {
        const parsedValue = valueFormatter
          ? valueFormatter(targetValue)
          : targetValue;

        if (tableField) {
          const newValue = { ...data, [field]: parsedValue?.trim() };
          const fieldValues = [...form.getValues(tableField)];
          fieldValues[rowIndex] = newValue;
          form.setValue(tableField, fieldValues);
        } else {
          form.setValue(field, parsedValue);
        }

        setValue && setValue(parsedValue);
      };

      return (
        <MemoizedFormField
          control={form.control}
          name={field}
          render={() => {
            return (
              <MemoizedInput
                onKeyDownCapture={e => {
                  if (
                    [
                      'ArrowUp',
                      'ArrowDown',
                      'ArrowLeft',
                      'ArrowRight',
                      'Tab',
                    ].includes(e.code)
                  )
                    e.stopPropagation();
                }}
                containerClassName="flex-1"
                size={32}
                placeholder={placeholder}
                onChange={e => {
                  e.preventDefault();
                  onChange(e.target.value);
                }}
                value={value?.toString()}
                onClear={() => onChange('')}
              />
            );
          }}
        />
      );
    },
    headerName,
  };
};

export const createCustomCol = ({
  field,
  ...props
}: createColProps): ColDef => {
  return {
    flex: 1,
    field,
    headerClass: 'ag-right-aligned-cell',
    cellStyle: { textAlign: 'center' },
    ...(props?.size && ColSize[props.size]),
    ...props,
  };
};

export const createImagePreviewCol = ({
  field,
  width,
  height,
  isPreviewRatioFixed,
  ...props
}: createColProps & {
  width?: number;
  height?: number;
  isPreviewRatioFixed?: boolean;
}): ColDef => {
  return {
    field,
    headerClass: 'ag-right-aligned-cell',
    cellStyle: { textAlign: 'center' },
    width: 129,
    minWidth: 129,
    maxWidth: 129,
    flex: 1,
    cellRenderer: ({ value, valueFormatted }: ICellRendererParams) => {
      const imgSrc: string = valueFormatted || value;
      if (!imgSrc) return '-';

      return (
        <ImagePreview
          imgSrc={imgSrc}
          width={width || 40}
          height={height || 40}
          isPreviewRatioFixed={isPreviewRatioFixed ?? true}
        />
      );
    },
    ...(props?.size && ColSize[props.size]),
    ...props,
  };
};

export const createAdministratorNameCol = ({
  ...props
}: Omit<createColProps, 'field'>): ColDef => {
  return {
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    field: 'adminName',
    width: 147,
    minWidth: 147,
    maxWidth: 200,
    flex: 1,
    ...(props?.size && ColSize[props.size]),
    ...props,
    cellRenderer: (params: ValueGetterParams) => {
      return params?.data?.administrator?.name || '-';
    },
  };
};

export const createBadgeCol = ({
  field,
  options,
  ...props
}: createColProps & {
  options: {
    label: string;
    value: string;
    color: keyof typeof badgeColorData;
  }[];
}) => {
  return {
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    field,
    width: 143,
    minWidth: 143,
    maxWidth: 200,
    ...(props?.size && ColSize[props.size]),
    ...props,
    cellRenderer: ({ value }: ICellRendererParams) => {
      const target = options.find(item => item.value === value) || {
        label: '-',
        color: 'lightGray',
      };

      return <Badge badgeColor={target.color}>{target.label}</Badge>;
    },
  };
};

export const createDateRangeCol = ({
  field,
  keys,
  ...props
}: createColProps & { keys: [string, string] }) => {
  return {
    field,
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    ...ColSize.md,
    ...props,
    cellRenderer: ({ data }: ICellRendererParams) =>
      `${dayjs(data?.[keys?.[0]]).format('YYYY-MM-DD')} ~ ${dayjs(
        data?.[keys?.[1]],
      ).format('YYYY-MM-DD')}`,
  };
};

const _renderText = (value: string | number) => (
  <Typo typoType="b14r">{value}</Typo>
);
